import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import {
  Grid,
  Button,
  Divider,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import theme from 'src/theme';
import Loader from 'src/common/Loader';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import { GROUP_TYPES } from 'src/utils/constants';
import { addNewVehicle, updateVehicleDetails } from 'src/redux/vehicleSlice';
import { getAllSites } from 'src/redux/siteSlice';
import { getAllContractorList, getGroupList } from 'src/redux/groupSlice';
import { SwitchInput } from 'src/common/FormElements';
import InputField from 'src/common/formFields/InputField';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import AutocompleteField from 'src/common/formFields/AutocompleteField';
import SelectField from 'src/common/formFields/SelectField';
import { getAllTanks } from 'src/redux/tankSlice';
import {
  vehicleFormModel,
  vehicleFormInitialValues,
  getVehicleValidationSchema
} from './vehicleFormModel';

const LEGACY_SEMI_MOVABLE_OPTION = {
  id: 'Semi Movable',
  name: 'Semi Movable'
};

const NewVehicle = ({
  open,
  handleClose,
  initialValues,
  prefillValues,
  onSuccess
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const [vehicleGroupList, setVehicleGroupList] = useState([]);
  const [contractorList, setContractorList] = useState([]);
  const { allSites } = useSelector((state) => state.sites);
  const { allTanks } = useSelector((state) => state.tanks);
  const { appSettings } = useSelector((state) => state.settings);
  const assetCodeMappingEnabled = Boolean(appSettings?.asset_code_mapping_enabled);
  const globalTyreEnabled = appSettings?.tyre_management_v2_enabled === true;
  const validationSchema = getVehicleValidationSchema({
    assetCodeMappingEnabled,
    isEdit: Boolean(initialValues),
  });
  const requiredHintText = '';

  let formInitValues = vehicleFormInitialValues;

  if (initialValues) {
    formInitValues = {
      ...initialValues,
      model: initialValues.model ?? '',
      contractor: initialValues.contractor?.id
        || initialValues.contractor_id
        || initialValues.contractor
        || '',
      standard_mileage: Number(initialValues.standard_mileage) > 0
        ? initialValues.standard_mileage
        : '',
      show_omnicomm_log_ignition: initialValues.show_omnicomm_log_ignition ?? true,
      show_omnicomm_log_engine: initialValues.show_omnicomm_log_engine ?? true,
      show_omnicomm_log_secondary_engine: initialValues.show_omnicomm_log_secondary_engine ?? false,
      omnicomm_secondary_engine_threshold: initialValues.omnicomm_secondary_engine_threshold ?? 8
    };
  } else if (prefillValues) {
    formInitValues = {
      ...vehicleFormInitialValues,
      ...prefillValues
    };
  }
  const isEditMode = Boolean(initialValues);
  const NULL_GROUP_OPTION = { id: null, name: '--------' };

  const withNullOption = (list = []) => [NULL_GROUP_OPTION, ...list];

  const {
    formId,
    formField: {
      vehicleGroup,
      site,
      department,
      contractor,
      vehicleCategory,
      transferTank,
      registrationNum,
      vehicleType,
      assetCode,
      assetType,
      tankCapacity,
      telemeticsEnabled,
      telemeticsCategory,
      fleetServiceProvider,
      showOmnicommLogIgnition,
      showOmnicommLogEngine,
      showOmnicommLogSecondaryEngine,
      omnicommSecondaryEngineThreshold,
      serviceInterval,
      vehicleModel,
      fuelType,
      maxDiffAllowed,
      odometerReading,
      monthlyFixedEngineHours,
      standardMileage,
      poNumber,
      tyreManagementEnabled
    }
  } = vehicleFormModel;

  useEffect(() => {
    if (open) {
      dispatch(getAllSites());
      dispatch(getAllTanks());
      dispatch(getGroupList({ groupType: GROUP_TYPES[1].id }))
        .unwrap()
        .then((list) => setDepartmentList(withNullOption(list)));
      dispatch(getGroupList({ groupType: GROUP_TYPES[0].id }))
        .unwrap()
        .then((list) => setVehicleGroupList(withNullOption(list)));
      dispatch(getAllContractorList())
        .unwrap()
        .then((list) => setContractorList(withNullOption(list)));
    }
  }, [open, dispatch]);

  const onSubmit = async (values, actions) => {
    const payload = { ...values };
    const standardMileageValue = Number(payload.standard_mileage);

    if (!payload.standard_mileage || Number.isNaN(standardMileageValue) || standardMileageValue <= 0) {
      delete payload.standard_mileage;
    }

    if (!values.telemetics_enabled) {
      payload.telemetics_category = '';
      payload.fleet_service_provider = '';
    }

    // The legacy workflow must not send a V2-only field while the company
    // feature is disabled (including missing or failed settings responses).
    if (!globalTyreEnabled) {
      delete payload.tyre_management_enabled;
    }

    setLoading(true);
    try {
      let result;
      if (initialValues) {
        result = await dispatch(updateVehicleDetails(payload)).unwrap();
      } else {
        result = await dispatch(addNewVehicle(payload)).unwrap();
      }
      handleClose();
      if (onSuccess) {
        onSuccess(result);
      }
      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Vehicle details updated successfully!'
            : 'New vehicle has been created successfully!'
        })
      );
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'UPDATE VEHICLE' : 'ADD NEW VEHICLE'}
        onCancel={handleClose}
      />

      <Divider />

      {loading ? (
        <Loader />
      ) : (
        <Formik
          initialValues={formInitValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values }) => {
            const legacySemiMovableSelected = initialValues?.[vehicleType.name] === LEGACY_SEMI_MOVABLE_OPTION.id
              && values[vehicleType.name] === LEGACY_SEMI_MOVABLE_OPTION.id;

            const vehicleTypeOptions = legacySemiMovableSelected
              ? [...vehicleType.data, LEGACY_SEMI_MOVABLE_OPTION]
              : vehicleType.data;

            return (
              <Form id={formId}>
                {/* SCROLLABLE CONTENT */}
                <DialogContent
                  sx={{
                    maxHeight: '65vh',
                    overflowY: 'auto',
                    px: 3,
                    py: 2,
                    backgroundColor: theme.palette.background.default
                  }}
                >
                  {!isEditMode && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      * Required fields
                    </Typography>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <InputField name={registrationNum.name} label={`${registrationNum.label} *`} required requiredHintText={requiredHintText} fullWidth />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={vehicleType.name}
                        label={`${vehicleType.label} *`}
                        data={vehicleTypeOptions}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={assetCode.name}
                        label={`${assetCode.label} *`}
                        upperCase={!assetCodeMappingEnabled || Boolean(initialValues)}
                        showErrorOnChange={assetCodeMappingEnabled && !initialValues}
                        showErrorOnChangeMessages={assetCodeMappingEnabled && !initialValues
                          ? ['Asset Code must be exactly 3 or 4 digits']
                          : undefined}
                        inputProps={assetCodeMappingEnabled && !initialValues
                          ? { inputMode: 'numeric', maxLength: 4 }
                          : undefined}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField name={tankCapacity.name} label={`${tankCapacity.label} *`} required requiredHintText={requiredHintText} fullWidth />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={vehicleCategory.name}
                        label={vehicleCategory.label}
                        data={vehicleCategory.data}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={transferTank.name}
                        label={values[vehicleCategory.name] === 'TRNSFR' ? `${transferTank.label} *` : transferTank.label}
                        data={allTanks}
                        disabled={values[vehicleCategory.name] !== 'TRNSFR'}
                        required={values[vehicleCategory.name] === 'TRNSFR'}
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField name={vehicleModel.name} label={vehicleModel.label} fullWidth />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField name={site.name} label={`${site.label} *`} data={allSites} required requiredHintText={requiredHintText} fullWidth />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={fuelType.name}
                        label={`${fuelType.label} *`}
                        data={fuelType.data}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={vehicleGroup.name}
                        label={vehicleGroup.label}
                        data={vehicleGroupList}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={department.name}
                        label={department.label}
                        data={departmentList}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <AutocompleteField
                        name={contractor.name}
                        label={contractor.label}
                        options={contractorList}
                        placeholder="Search contractor"
                        getOptionLabel={(option) => option?.name || ''}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SwitchInput name={telemeticsEnabled.name} label={telemeticsEnabled.label} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={telemeticsCategory.name}
                        label={values[telemeticsEnabled.name] ? `${telemeticsCategory.label} *` : telemeticsCategory.label}
                        data={telemeticsCategory.data}
                        disabled={!values[telemeticsEnabled.name]}
                        required={values[telemeticsEnabled.name]}
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={fleetServiceProvider.name}
                        label={fleetServiceProvider.label}
                        data={fleetServiceProvider.data}
                        disabled={!values[telemeticsEnabled.name]}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                        Omnicomm log report columns
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <SwitchInput name={showOmnicommLogIgnition.name} label={showOmnicommLogIgnition.label} />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <SwitchInput name={showOmnicommLogEngine.name} label={showOmnicommLogEngine.label} />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <SwitchInput name={showOmnicommLogSecondaryEngine.name} label={showOmnicommLogSecondaryEngine.label} />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <InputField
                        name={omnicommSecondaryEngineThreshold.name}
                        label={omnicommSecondaryEngineThreshold.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField name={odometerReading.name} label={`${odometerReading.label} *`} required requiredHintText={requiredHintText} fullWidth />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField name={standardMileage.name} label={standardMileage.label} fullWidth />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField name={poNumber.name} label={poNumber.label} fullWidth />
                    </Grid>

                    <Grid item xs={12}>
                      {globalTyreEnabled && (
                        <SwitchInput
                          name={tyreManagementEnabled.name}
                          label={tyreManagementEnabled.label}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={monthlyFixedEngineHours.name}
                        label={monthlyFixedEngineHours.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={serviceInterval.name}
                        label={serviceInterval.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={maxDiffAllowed.name}
                        label={maxDiffAllowed.label}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={assetType.name}
                        label={assetType.label}
                        data={assetType.data}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </DialogContent>

                <Divider />

                {/* STICKY FOOTER */}
                <DialogActions
                  sx={{
                    px: 3,
                    py: 2,
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: theme.palette.background.paper,
                    justifyContent: 'flex-end',
                    gap: 2
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleClose}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 4,
                      fontWeight: 600
                    }}
                  >
                    {initialValues ? 'Update Vehicle' : 'Add Vehicle'}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      )}
    </FMSModal>
  );
};

NewVehicle.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  prefillValues: PropTypes.object,
  onSuccess: PropTypes.func
};

export default NewVehicle;
